// react
import { useState } from 'react';

// i18n
import { useTranslation } from 'react-i18next';

// mui
import {
	Alert,
	Box,
	BoxProps,
	Chip,
	CircularProgress,
	IconButton,
	List,
	ListItem,
	ListItemText,
	Paper,
	Skeleton,
	Stack,
	Tooltip,
} from '@mui/material';

// icons
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';

// types
import { AiDocument } from '../../../../@types';

// ui
import { SectionHeader } from '../../../ui/SectionHeader/SectionHeader';

// services
import { downloadAiDocument } from '../../../../services/ai';

// utils
import { formatDate } from '../../../../utils/dates';

// props
export interface DocumentsProps {
	documents: AiDocument[];
	isLoading: boolean;
}

export const Documents = ({ documents, isLoading }: DocumentsProps) => {
	// hooks
	const { t } = useTranslation();

	// state
	const [downloading, setDownloading] = useState<string | null>(null);

	const handleDownload = async (fileName: string) => {
		setDownloading(fileName);
		try {
			await downloadAiDocument(fileName);
		} catch {
			/* silent — the row stays put; the user can retry */
		} finally {
			setDownloading(null);
		}
	};

	// vars
	const BoxProps: BoxProps = {};
	const hasDocuments = documents.length > 0;

	let content = null;

	if (isLoading) {
		content = <Skeleton height={80} variant="rounded" />;
	} else if (hasDocuments) {
		BoxProps.sx = { p: { xs: 2, sm: 3 } };

		content = (
			<List dense disablePadding>
				{documents.map((d, idx) => {
					const meta = d.metadata || {};
					const rawSize =
						typeof meta['file_size_bytes'] === 'number'
							? (meta['file_size_bytes'] as number)
							: typeof meta['file_size'] === 'number'
								? (meta['file_size'] as number)
								: typeof meta['size'] === 'number'
									? (meta['size'] as number)
									: null;
					const sizeLabel =
						rawSize != null
							? rawSize >= 1024 * 1024
								? `${(rawSize / (1024 * 1024)).toFixed(1)} MB`
								: `${Math.max(1, Math.round(rawSize / 1024))} KB`
							: null;
					const uploadedAt = d.uploaded_at || d.created_at || null;
					const dateLabel = uploadedAt ? formatDate({ date: new Date(uploadedAt) }) : null;
					const uploadedByName =
						typeof d.uploaded_by === 'object' && d.uploaded_by
							? d.uploaded_by.displayName ||
								d.uploaded_by.email ||
								(d.uploaded_by.id ? `User #${d.uploaded_by.id}` : null)
							: null;
					const fileName = d.filename || d.file_name || null;
					const displayName = fileName || `#${d.id}`;
					const metaParts = [sizeLabel, dateLabel, uploadedByName].filter(Boolean) as string[];
					const isLast = idx === documents.length - 1;
					return (
						<ListItem
							key={d.id}
							divider={!isLast}
							secondaryAction={
								<Stack direction="row" spacing={0.5} alignItems="center">
									{d.doc_type ? (
										<Chip label={d.doc_type} size="small" variant="outlined" />
									) : null}
									{fileName && (
										<Tooltip title={t('download')}>
											<span>
												<IconButton
													size="small"
													edge="end"
													disabled={downloading === fileName}
													onClick={() => handleDownload(fileName)}>
													{downloading === fileName ? (
														<CircularProgress size={16} />
													) : (
														<DownloadOutlinedIcon fontSize="small" />
													)}
												</IconButton>
											</span>
										</Tooltip>
									)}
								</Stack>
							}>
							<ListItemText primary={displayName} secondary={metaParts.join(' · ') || null} />
						</ListItem>
					);
				})}
			</List>
		);
	} else {
		content = <Alert severity="info">{t('noDocuments')}</Alert>;
	}

	return (
		<Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
			<SectionHeader IconCircleProps={{ type: 'document' }} title={t('documents')} />
			<Box {...BoxProps}>{content}</Box>
		</Paper>
	);
};
